<template>
    <div
        style="max-width: 600px; margin: auto; text-align: left;"
        :class="field.options?.inline ? 'select-field-inline' : 'select-field-stack'"
    >
        <FieldHeader
            v-if="showHeader"
            :class="field.options?.inline ? 'q-mb-none q-mr-md select-field-header-inline' : 'q-mb-xs'"
            :field="field"
        />
        <div
            v-if="useHorizontalRadio"
            :class="field.options?.inline ? 'horizontal-radio-group select-field-input-inline' : 'horizontal-radio-group'"
            style="display: flex; flex-direction: row; gap: 16px;"
        >
            <div
                v-for="(opt, index) in colouredRadioOptions"
                :key="`${opt.value}-${index}`"
                class="column items-center text-center radio-label-content"
                style="flex: 1; justify-content: flex-end; cursor: pointer;"
                @click="!readOnly && onUpdate(opt.value)"
            >
                <div
                    class="column items-center text-center"
                    style="flex: 1; justify-content: flex-end;"
                >
                    <span
                        v-if="opt.emoji"
                        :class="modelValue === opt.value ? 'radio-emoji radio-emoji-selected' : 'radio-emoji'"
                    >{{ getEmoji(opt.emoji) }}</span>
                    <div v-if="opt.label" v-html="opt.label"></div>
                    <div v-if="opt.sublabel" class="text-caption" v-html="opt.sublabel"></div>
                </div>
                <q-radio
                    class="rainbow-radio q-mt-xs"
                    :style="{ '--radio-color': opt.rainbowColor }"
                    :model-value="modelValue"
                    :val="opt.value"
                    :disable="readOnly"
                    :label="undefined"
                    @update:model-value="onUpdate"
                />
            </div>
        </div>
        <q-select
            v-else
            filled
            dense
            :label="modelValue ? undefined : (field.options?.placeholder || `Please select ${field.options?.multiple ? 'your options' : 'an option'}...`)"
            :options="optionsWithEmojiHtml"
            :model-value="modelValue"
            :multiple="field.options?.multiple || false"
            :clearable="field.required ? false : true"
            option-value="value"
            option-label="labelHtml"
            options-html
            display-value-html
            :class="field.options?.inline ? 'select-field-input-inline' : ''"
            :readonly="readOnly"
            emit-value
            map-options
            @update:model-value="onUpdate"
        />

        <!-- Text input for options with hasTextInput -->
        <q-input
            v-if="selectedOptionHasTextInput"
            filled
            dense
            class="q-mt-sm"
            :class="field.options?.inline ? 'select-field-input-inline' : ''"
            :model-value="textInputValue"
            :readonly="readOnly"
            placeholder="Please specify..."
            @update:model-value="onTextInputUpdate"
            @blur="$emit('blur')"
        />
    </div>
</template>

<script>
    import FieldHeader from './FieldHeader.vue';

    export default {
        name: 'SelectField',
        components: { FieldHeader },
        props: {
            field: {
                type: Object,
                required: true,
            },
            options: {
                type: Array,
                default: () => ([]),
            },
            modelValue: {
                type: [String, Number, Boolean, Object, Array],
                default: '',
            },
            textInputValue: {
                type: String,
                default: '',
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            showHeader: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['update:modelValue', 'update:textInputValue', 'blur'],
        computed: {
            useHorizontalRadio() {
                return this.field?.options?.horizontal === true || this.field?.options?.horizontal === 'true';
            },
            colouredRadioOptions() {
                const optionCount = this.options.length;

                return this.options.map((option, index) => ({
                    ...option,
                    rainbowColor: this.getRainbowColor(index, optionCount),
                }));
            },
            // For the dropdown (non-horizontal) case, prepend emoji inline before the label HTML
            optionsWithEmojiHtml() {
                return this.options.map((option) => {
                    if (!option.emoji) return option;

                    return {
                        ...option,
                        labelHtml: `<span class="select-option-emoji">${option.emoji}</span>${option.labelHtml ?? option.label ?? ''}`,
                    };
                });
            },
            selectedOptionHasTextInput() {
                if (!this.modelValue) return false;

                const selectedValues = Array.isArray(this.modelValue)
                    ? this.modelValue
                    : [this.modelValue];

                return this.options.some(
                    (opt) => selectedValues.includes(opt.value) && opt.hasTextInput
                );
            },
        },
        methods: {
            getRainbowColor(index, totalOptions) {
                if (totalOptions <= 1) {
                    return 'hsl(0 85% 45%)';
                }

                const hue = (index / (totalOptions - 1)) * 120;
                return `hsl(${hue} 85% 45%)`;
            },
            onUpdate(value) {
                this.$emit('update:modelValue', value);

                // Clear text input if the newly selected option(s) don't have hasTextInput
                const selectedValues = Array.isArray(value) ? value : [value];
                const hasTextInput = this.options.some(
                    (opt) => selectedValues.includes(opt.value) && opt.hasTextInput
                );
                if (!hasTextInput) {
                    this.$emit('update:textInputValue', '');
                }

                this.$emit('blur');
            },
            onTextInputUpdate(value) {
                this.$emit('update:textInputValue', value);
            },
            getEmoji(emoji) {
                const emojiMap = {
                    'sad': '😞',
                    'unhappy': '😕',
                    'neutral': '😐',
                    'happy': '🙂',
                    'joyful': '😄',
                };
                return emojiMap[emoji] || emoji;
            }
        },
    };
</script>